"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { Message } from "@/lib/core/crm/types";
import { useInboxes } from "./useInboxes";

export interface Attachment {
  id: string;
  name: string;
  size?: number;
  mimeType?: string;
}

/**
 * The OAuth connection a message's mailbox belongs to.
 *
 * The connection id lives on the INBOX, not the message — `Message` only
 * carries `inboxId`. `/api/messages/attachments` needs the connection id
 * because that is what it authorizes against (`resolveConnectionAccess`) and
 * what it resolves the provider token from; it no longer takes a
 * client-supplied provider token, and it no longer trusts a client-supplied
 * provider name either.
 */
export function useMessageConnectionId(message: Message | null): string | null {
  const { inboxes } = useInboxes();
  return useMemo(() => {
    if (!message) return null;
    const inbox = inboxes.find((candidate) => candidate.id === message.inboxId);
    return inbox?.connectionId ?? null;
  }, [inboxes, message]);
}

export function useMessageAttachments(message: Message | null, enabled: boolean = true) {
  const { session } = useUniversalAuth();
  const connectionId = useMessageConnectionId(message);

  return useQuery({
    queryKey: ["message-attachments", message?.id, connectionId],
    queryFn: async (): Promise<Attachment[]> => {
      if (!message || !connectionId) return [];

      const params = new URLSearchParams({
        connectionId,
        messageId: message.providerMessageId,
      });
      const response = await fetch(`/api/messages/attachments?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch attachments");
      }

      const data = await response.json();
      return data.attachments || [];
    },
    enabled: enabled && !!message && !!connectionId && !!session?.access_token,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
